import type { ConnectorProviderId } from "@/components/connectors/connector-options";
import { connectorRequiresApiKey } from "@/components/connectors/connector-options";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import type { ConnectorDto } from "@/lib/gen-api";
import {
  groupConnectorControllerActivateConnector,
  groupConnectorControllerCreateSessionToken,
  groupConnectorControllerDeleteConnector,
  groupConnectorControllerListConnectors,
  groupConnectorControllerUpdateConnector,
} from "@/lib/gen-api";
import { connectNangoProvider } from "@/lib/nango-client";
import { FIREFLIES_NANGO_INTEGRATION_ID } from "@/lib/nango-integrations.constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const NANGO_INTEGRATION_IDS: Record<ConnectorProviderId, string> = {
  FIREFLIES: FIREFLIES_NANGO_INTEGRATION_ID,
};

export function useConnectorsData() {
  const groupUuid = useGroupUuid();
  const queryClient = useQueryClient();

  const { data: connectors = [], isLoading } = useQuery({
    queryKey: ["connectors", groupUuid],
    enabled: !!groupUuid,
    queryFn: async (): Promise<ConnectorDto[]> => {
      const response = await groupConnectorControllerListConnectors({
        path: { groupUuid: groupUuid! },
      });
      return response.data?.data ?? [];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      connectorUuid,
      customName,
    }: {
      connectorUuid: string;
      customName: string;
    }) => {
      const response = await groupConnectorControllerUpdateConnector({
        path: { groupUuid: groupUuid!, connectorUuid },
        body: { custom_name: customName },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["connectors", groupUuid],
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (connectorUuid: string) => {
      await groupConnectorControllerDeleteConnector({
        path: { groupUuid: groupUuid!, connectorUuid },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["connectors", groupUuid],
      });
    },
  });

  const connectMutation = useMutation({
    mutationFn: async ({
      provider,
      connectionName,
      apiKey,
    }: {
      provider: ConnectorProviderId;
      connectionName: string;
      apiKey?: string;
    }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      const nangoIntegrationId = NANGO_INTEGRATION_IDS[provider];
      if (!nangoIntegrationId) {
        throw new Error(`Connector ${provider} is not supported yet`);
      }

      if (connectorRequiresApiKey(provider) && !apiKey?.trim()) {
        throw new Error("API key is required");
      }

      const sessionResponse = await groupConnectorControllerCreateSessionToken({
        path: { groupUuid },
        body: { provider },
      });

      if (!sessionResponse.data?.token) {
        throw new Error("Failed to create session token");
      }

      const nangoResult = await connectNangoProvider(
        sessionResponse.data.token,
        nangoIntegrationId,
        apiKey ? { apiKey } : undefined,
      );

      const activateResponse = await groupConnectorControllerActivateConnector({
        path: { groupUuid },
        body: {
          connectionId: nangoResult.connectionId,
          provider,
          custom_name: connectionName,
        },
      });

      return activateResponse.data?.connector;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["connectors", groupUuid],
      });
    },
  });

  return {
    connectors,
    isLoading,
    updateConnector: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteConnector: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    connectConnector: connectMutation.mutateAsync,
    isConnecting: connectMutation.isPending,
  };
}
